import React from 'react';
import { useNavigate } from 'react-router-dom';

// Small stateless helpers shared across MemorialAdmin tabs.

export function Field({ label, children }) {
  return <div><label className="ml-label">{label}</label>{children}</div>;
}

export function Stat({ label, value }) {
  return (
    <div className="ml-card !p-5">
      <div className="ml-overline">{label}</div>
      <div className="font-serif text-3xl mt-1 text-[hsl(var(--primary))]">{value}</div>
    </div>
  );
}

export function InfoCard({ label, value, hint }) {
  return (
    <div className="ml-card !p-5">
      <div className="font-serif text-2xl text-[hsl(var(--primary))]">{value}</div>
      <div className="text-sm mt-1 text-[hsl(var(--primary))]">{label}</div>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{hint}</div>
    </div>
  );
}

/**
 * Subtle, respectful upgrade nudge — only rendered when the family is near
 * (≥80%) or at the photo/admin/storage cap on their current plan. Never shown
 * on the top-tier "Legacy" plan.
 */
const NEXT_PLAN = { classic: 'Gold', gold: 'Legacy' };
export function UpgradeNudge({ summary, variant = 'banner', scope = 'all' }) {
  const nav = useNavigate();
  const planId = summary?.plan?.id;
  const next = NEXT_PLAN[planId];
  if (!next) return null;

  const limits = summary.plan.limits || {};
  const c = summary.counts || {};
  const st = summary.storage || { mb_used: 0, mb_limit: 1 };

  const checks = [];
  if (scope === 'all' || scope === 'media') {
    const pct = limits.photos_total ? c.media_total / limits.photos_total : 0;
    if (pct >= 1) checks.push({ key: 'media', msg: `You've reached your ${summary.plan.name} plan's ${limits.photos_total}-photo limit.` });
    else if (pct >= 0.8) checks.push({ key: 'media', msg: `You've used ${c.media_total} of ${limits.photos_total} photos on your ${summary.plan.name} plan.` });
  }
  if (scope === 'all' || scope === 'admins') {
    const pct = limits.admins ? c.admins / limits.admins : 0;
    if (pct >= 1) checks.push({ key: 'admins', msg: `You've reached your ${summary.plan.name} plan's ${limits.admins}-admin limit.` });
    else if (pct >= 0.8) checks.push({ key: 'admins', msg: `You've added ${c.admins} of ${limits.admins} family admins on your ${summary.plan.name} plan.` });
  }
  if (scope === 'all' || scope === 'storage') {
    const pct = st.mb_limit ? st.mb_used / st.mb_limit : 0;
    if (pct >= 0.9) checks.push({ key: 'storage', msg: `Your ${summary.plan.name} plan storage is ${Math.round(pct * 100)}% full.` });
  }
  if (checks.length === 0) return null;

  const headline = checks[0].msg;
  const body = `Continue preserving more memories with ${next}.`;

  if (variant === 'inline') {
    return (
      <div
        data-testid="upgrade-nudge-inline"
        className="mt-4 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 px-4 py-3 text-sm text-[hsl(var(--primary))] flex flex-wrap items-center justify-between gap-3"
      >
        <div>
          <span className="font-medium">{headline}</span>{' '}
          <span className="text-[hsl(var(--muted-foreground))]">{body}</span>
        </div>
        <button
          onClick={() => nav(`/dashboard/m/${summary.memorial.memorial_id}?tab=billing`)}
          className="text-xs uppercase tracking-widest font-semibold text-[hsl(var(--accent))] hover:underline"
          data-testid="upgrade-nudge-cta-inline"
        >
          View {next} plan →
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="upgrade-nudge-banner"
      className="ml-card border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/5 flex flex-wrap items-center justify-between gap-4"
    >
      <div>
        <div className="ml-overline">A gentle note</div>
        <div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">{headline}</div>
        <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{body}</div>
      </div>
      <button
        onClick={() => nav(`/dashboard/m/${summary.memorial.memorial_id}?tab=billing`)}
        className="ml-btn-outline !py-2 !px-5"
        data-testid="upgrade-nudge-cta"
      >
        Explore {next}
      </button>
    </div>
  );
}
